import React, { useEffect, useState } from 'react';
import { IAxiosGetResponse, IServiceAggregatorServiceDto, IServiceDto, IServicesDifferenceDto, ITransformationsServiceDto, ISyncType } from '../../interfaces/IUtilities';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getById, updateById } from '../_request';
import Loader from '../../modules/layout/components/Loader';
import CapitalizeFirstLetter from '../../helpers/Utils';
import ConfirmModal from '../../modules/layout/components/ConfirmModal';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import BackButton from '../../modules/layout/components/BackButton';
import { ArrowClockwise } from 'react-bootstrap-icons';


const serviceSAFields: (keyof IServiceDto)[] = [
  "id", "name", "code", "isDeleted"
];

const serviceTRFields: (keyof ITransformationsServiceDto)[] = [
  "id", "name", "code", "isDeleted"
]; //"createdAt", "modifiedAt"

const trFieldKeyMap: Record<keyof ITransformationsServiceDto, string> = {
  id: "id",
  name: "tr-name",
  code: "tr-code",
  createdAt: '',
  modifiedAt: '',
  isDeleted: 'tr-isDeleted'
};

const ServiceDataDifferences = () => { 
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [diffSAData, setDiffSAData] = useState({} as IServicesDifferenceDto);
  const [diffTRData, setDiffTRData] = useState({} as IServicesDifferenceDto);
  const [showModal, setShowModal] = useState(false);
  const [enableSASync, setEnableSASync] = useState(false);  
  const [enableTRSync, setEnableTRSync] = useState(false);  
  const [titleSync, setTitleSync] = useState('');  
  const [syncBtn, setSyncBtn] = useState('');

  const fetchSAData = async () => {
      try {
        let result = await getById('/api/services/comparesaservices/', id!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
          let data = result.data as IServicesDifferenceDto;
          setDiffSAData(data);
       
          let saSync = (data?.diffColumnsList?.some(col => !col.startsWith("tr-")) || data?.serviceAggregatorService === null) ?? false;    
          setEnableSASync(saSync);    
          setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else{
            showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate("/services/list");
      }
  };

  const fetchTRData = async () => {
      try {
        let result = await getById('/api/services/comparetrservices/', id!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
          let data = result.data as IServicesDifferenceDto;
          setDiffTRData(data);

          let trSync = (data?.diffColumnsList?.some(col => col.startsWith("tr-")) || data?.transformationsService === null) ?? false;
          setEnableTRSync(trSync);        
          setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else{
            showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate("/services/list");
      }
  };

  useEffect(() => {
    const fetchAll = async () => { 
      await Promise.all([fetchSAData(), fetchTRData()]);
    };
    fetchAll();
  }, []);

  const handleSyncSA = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Service Aggregator" }));
    setSyncBtn(ISyncType.ServiceAggregator);
  };

  const handleSyncTR = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Transformations" }));
    setSyncBtn(ISyncType.Transformations);
  };

  const handleConfirmSync = async() => {
    try {
      setLoading(true);
      let result = {} as IAxiosGetResponse;
      let respStatus;

      if (syncBtn === ISyncType.ServiceAggregator) {
        result = await updateById('/api/services/syncsaservices', id!) as IAxiosGetResponse;
        respStatus = result?.response.status;
      }
      else if (syncBtn === ISyncType.Transformations) {
        result = await updateById('/api/services/synctrservices', id!) as IAxiosGetResponse;
        respStatus = result?.response.status;
      }

      if (respStatus === 200) {
        let isSync = result.data as boolean;
        setLoading(true);

        if (syncBtn === ISyncType.ServiceAggregator) {
          await fetchSAData();
        }
        else if (syncBtn === ISyncType.Transformations) {
          await fetchTRData();
        }

        if(isSync) {
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
          }
          else {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Transformations" }), 'success');
          }
        }
        else{
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddFail', { database: "Service Aggregator" }), 'error');
          }
          else {
            showToast(t('SyncInDatabaseAddFail', { database: "Transformations" }), 'error');
          }
        }
      }
      else if (respStatus === 403) {
          navigate("/error/403");
      }
      else if (respStatus === 401) {
          navigate("/auth/login");
      }
      else if (respStatus === 500) {
          navigate("/error/500");
      }
      else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
        setLoading(false);
        console.log(t('ConnectionError', { error: String(error) }));
        showToast(t('ConnectionError', { error: String(error) }), 'error');
        navigate("/services/list");
    }

    setShowModal(false);
  };
  
  const handleCancelSync = () => {
    setShowModal(false);
  };

  const handleRefreshClick = async (btnName: string) => {
    setLoading(true);
    try {
      if (btnName === ISyncType.ServiceAggregator) {
        await fetchSAData();
        showToast(t('DatabaseTableRefreshed', { database: "Service Aggregator" }), 'success');
      }
      else if (btnName === ISyncType.Transformations) {
        await fetchTRData();
        showToast(t('DatabaseTableRefreshed', { database: "Transformations" }), 'success');
      }
    } catch (error) {
      setLoading(false);
      console.log(t('RefreshError'), error);
      showToast(`${t('FailedToRefresh')} ${btnName === ISyncType.ServiceAggregator ? 'Service Aggregator' : 'Transformations'} ${t('Table')}`, 'error');
    }
    setLoading(false);
  };

  return (
    <div className="container mt-4 gg-servicediff">
      <AppBreadcrumb 
        items={[
            { title: t('Home'), href: "/" },
            { title: t('Services'), href: "/services/list" },
            { title: `${diffSAData.adminWebService ? diffSAData.adminWebService?.name : ""}: ${t('ServiceDataDifferences')}` } // Active page (no href)
        ]} />
        
      <h3>{t('ServicesDataDifferences')}</h3>

      {loading && (<Loader />)}

      {(diffSAData || diffTRData) ?
        <div className="gg-service-diff">
          <h5>{t('RecordsFromDatabases')}</h5>

            <div key={diffSAData.id} className="my-3 border rounded p-3 shadow">
              <h4 className="text-start font-bold mb-2">{t('ServiceID')}: {diffSAData.id}</h4>

              <div className="row gg-service-sa-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceSAFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebService', 'serviceAggregatorService'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebService'
                              ? (diffSAData.adminWebService as IServiceDto)
                              : (diffSAData.serviceAggregatorService as IServiceAggregatorServiceDto);

                          return (
                            <tr key={`${diffSAData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffSAData.diffColumnsList?.some(col => !col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebService' ? 'Admin Web' : 'Service Aggregator'} {t('Database')}
                              </td>

                              {serviceSAFields.map((field) => {
                                const value = sourceData?.[field as keyof typeof sourceData];
                                const isDifferent = diffSAData.diffColumnsList?.includes(field);

                                switch (diffSAData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );
                                    
                                  case 1:
                                    return diffSAData.serviceAggregatorService ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {String(value)}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebService' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'serviceAggregatorService' ? '-' : String(value)}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.ServiceAggregator)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableSASync} className="btn btn-warning"
                    onClick={handleSyncSA}>{t('SyncIn', { database: "Service Aggregator" })}</button>
                </div>
              </div>

              <div className="row gg-service-tr-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceTRFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebService', 'transformationsService'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebService'
                              ? (diffTRData.adminWebService as IServiceDto)
                              : (diffTRData.transformationsService as ITransformationsServiceDto);

                          return (
                            <tr key={`${diffTRData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffTRData.diffColumnsList?.some(col => col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebService' ? 'Admin Web' : 'Transformations'} {t('Database')}
                              </td>

                              {serviceTRFields.map((field) => {
                                const backendKey = trFieldKeyMap[field];
                                const value = sourceData?.[field as keyof typeof sourceData];
                                const isDifferent = diffTRData.diffColumnsList?.includes(backendKey);

                                switch (diffTRData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );

                                  case 1:
                                    return diffTRData.transformationsService ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {String(value)}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebService' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'transformationsService' ? '-' : String(value)}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.Transformations)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableTRSync} className="btn btn-warning"
                    onClick={handleSyncTR}>{t('SyncIn', { database: "Transformations" })}</button>
                </div>
              </div>

            </div>
        </div>
      : <p className="text-warning">{t('NoDataDifferencesFoundBetweenAWAndDatabases')}</p>}

      <ConfirmModal
        show={showModal}
        title={titleSync}
        message={t('SyncWithDatabase')}
        onConfirm={handleConfirmSync}
        onCancel={handleCancelSync}
        buttonClass="warning"
        loading={loading}
      />
      
      <div className="row">
        <div className="col d-flex justify-content-between align-items-end">
          <BackButton goTo={"/services/list"} />
        </div>
      </div>

    </div>
  );
};

export default ServiceDataDifferences;
